import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { iconFor } from "@/lib/contentIcons";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import Seo from "@/components/Seo";
import { MaskLines, FadeIn, Reveal } from "@/components/common/Reveal";
import Marquee from "@/components/common/Marquee";
import LogoMarquee from "@/components/common/LogoMarquee";
import HelpCTA from "@/components/common/HelpCTA";
import WhatsAppButton from "@/components/common/WhatsAppButton";
import ProductCard from "@/components/product/ProductCard";
import PartnerLogo from "@/components/partner/PartnerLogo";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useSite,
  useSiteImages,
  useCategories,
  usePartners,
  useServices,
  useWhyAthena,
  useCustomers,
} from "@/context/SiteDataContext";
import { productsService } from "@/services/productsService";
import { HERO_SRCSET, OG_IMAGE, srcSetFor } from "@/data/images";

function Hero() {
  const IMAGES = useSiteImages();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section ref={ref} className="relative bg-white overflow-hidden">
      <div className="container-x pt-14 pb-0 md:pt-20">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6">
            <FadeIn>
              <p className="eyebrow">Athena Scientific · Singapore</p>
            </FadeIn>
            <h1 className="mt-6 text-[2.7rem] leading-[1.02] sm:text-6xl lg:text-[4.6rem] font-bold tracking-tight text-ink">
              <MaskLines lines={["Driven by Science.", "Focused on You."]} />
            </h1>
            <FadeIn delay={0.5}>
              <p className="mt-7 text-lg text-greyish max-w-lg">
                We help laboratories find the right technologies for their applications — combining trusted scientific instruments with technical expertise, application support and responsive after-sales service.
              </p>
            </FadeIn>
            <FadeIn delay={0.65}>
              <div className="mt-9 flex flex-wrap gap-3">
                <Link to="/products" data-testid="hero-explore-btn" className="inline-flex items-center gap-2 bg-aqua text-white px-7 py-4 rounded-md font-semibold hover:bg-aqua-dark transition-all group">
                  Explore Our Solutions <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link to="/contact" data-testid="hero-help-btn" className="inline-flex items-center gap-2 border border-border text-ink px-7 py-4 rounded-md font-semibold hover:border-ink/40 hover:bg-[#f6f8f9] transition-all">
                  How Can We Help You?
                </Link>
              </div>
            </FadeIn>
          </div>

          <div className="lg:col-span-6">
            <motion.div style={{ y }} className="relative">
              <div className="relative overflow-hidden rounded-lg aspect-[4/5] bg-[#f6f8f9]">
                {/* The LCP element. It is rendered by JavaScript, so the
                    browser cannot discover it while parsing the HTML — hence the
                    preload in index.html. fetchPriority tells it to jump the
                    queue ahead of the below-the-fold imagery, and width/height
                    give it the aspect ratio before any bytes arrive so nothing
                    reflows around it. Never lazy-load this one. */}
                <motion.img
                  style={{ scale, objectPosition: "78% center" }}
                  src={IMAGES.hero}
                  srcSet={srcSetFor(IMAGES.hero, HERO_SRCSET)}
                  sizes="(min-width: 1024px) 50vw, 100vw"
                  width={1200}
                  height={839}
                  fetchPriority="high"
                  decoding="async"
                  alt="Athena Scientific laboratory specialist examining samples beside an analytical balance"
                  className="w-full h-full object-cover"
                />
              </div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1, duration: 0.7 }}
                className="absolute -bottom-6 -left-4 sm:left-6 bg-white rounded-lg shadow-[0_12px_40px_rgba(0,0,0,0.12)] p-5 max-w-[270px] border border-border"
              >
                <p className="text-sm text-greyish leading-relaxed">
                  Over a Decade of Scientific Expertise. Trusted by Laboratories.
                </p>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>

      <CustomerBand />
    </section>
  );
}

/**
 * Customer logos, scrolling. Falls back to the original band of capability
 * words until the client has uploaded logos, so the section is never empty.
 */
function CustomerBand() {
  const customers = useCustomers();
  const withLogos = customers.filter((customer) => customer.logo);

  if (withLogos.length) {
    return (
      <div className="mt-16 border-y border-border bg-[#f9fafb] py-8">
        <p className="text-center text-xs font-semibold uppercase tracking-widest text-greyish mb-6">
          Trusted Across Leading Laboratories
        </p>
        <LogoMarquee items={withLogos} />
      </div>
    );
  }

  return (
    <div className="mt-16 border-y border-border bg-[#f9fafb] py-4 text-sm font-semibold uppercase tracking-widest text-greyish">
      <Marquee
        items={[
          "Technical Expertise",
          "Reliable Technologies",
          "Application Support",
          "Preventive Maintenance",
          "Equipment Rental",
          "After-Sales Service",
          "Driven by Science",
        ]}
      />
    </div>
  );
}

function Categories() {
  const CATEGORIES = useCategories();

  return (
    <section className="container-x py-20 md:py-28">
      <Reveal>
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div>
            <p className="eyebrow">Explore by Category</p>
            <h2 className="mt-4 text-3xl md:text-5xl font-bold tracking-tight text-ink max-w-2xl text-balance">Precision technologies for every laboratory workflow</h2>
          </div>
          <Link to="/products" className="inline-flex items-center gap-1.5 text-sm font-semibold text-ink hover:text-aqua transition-colors shrink-0">
            View full catalogue <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </Reveal>
      <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {CATEGORIES.map((c, i) => (
          <Reveal key={c.slug} delay={(i % 4) * 0.08}>
            {/* bg-[#f4f6f7], not bg-ink: this shows through wherever an
                uploaded image is transparent, and a dark ground turned a
                transparent product PNG into a black card. */}
            <Link to={`/products?category=${c.slug}`} data-testid={`home-category-${c.slug}`} className="group block relative overflow-hidden rounded-lg aspect-[4/5] bg-[#f4f6f7]">
              {/* The image is optional — without one the card falls back to a
                  branded panel rather than a broken image. */}
              {c.image && (
                <img src={c.image} alt={c.name} loading="lazy" decoding="async" width={1200} height={1200} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              )}
              <div className={`absolute inset-0 ${c.image ? "bg-gradient-to-t from-ink/85 via-ink/25 to-transparent" : "bg-gradient-to-br from-ink via-ink to-aqua/40"}`} />
              <div className="absolute inset-0 p-7 flex flex-col justify-end text-white">
                <h3 className="text-2xl font-bold">{c.name}</h3>
                <p className="mt-2 text-sm text-white/80 line-clamp-2">{c.shortDescription}</p>
                <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-aqua group-hover:gap-3 transition-all">
                  Explore Products <ArrowRight className="w-4 h-4" />
                </span>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function Featured() {
  const [products, setProducts] = useState(null);
  useEffect(() => {
    productsService.getFeaturedProducts().then(setProducts);
  }, []);
  return (
    <section className="bg-[#f9fafb] border-y border-border">
      <div className="container-x py-20 md:py-28">
        <Reveal>
          <p className="eyebrow">Featured Products</p>
          <h2 className="mt-4 text-3xl md:text-5xl font-bold tracking-tight text-ink max-w-2xl text-balance">Selected instruments, ready for enquiry</h2>
        </Reveal>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {!products
            ? Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="bg-white border border-border rounded-lg overflow-hidden">
                  <Skeleton className="aspect-square w-full" />
                  <div className="p-5 space-y-3"><Skeleton className="h-3 w-1/2" /><Skeleton className="h-5 w-3/4" /><Skeleton className="h-10 w-full" /></div>
                </div>
              ))
            : products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
      </div>
    </section>
  );
}

function Why() {
  const WHY_ATHENA = useWhyAthena();

  return (
    <section className="container-x py-20 md:py-28">
      <div className="grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4">
          <Reveal>
            <p className="eyebrow">Why Athena Scientific</p>
            <h2 className="mt-4 text-3xl md:text-4xl font-bold tracking-tight text-ink text-balance">More than an equipment supplier — a scientific partner</h2>
            <p className="mt-5 text-greyish">Inspired by Athena, the symbol of wisdom and knowledge, we believe the right solution is the one that best fits your needs today while supporting tomorrow's goals.</p>
          </Reveal>
        </div>
        <div className="lg:col-span-8 grid sm:grid-cols-2 gap-x-8 gap-y-2">
          {WHY_ATHENA.map((w, i) => {
            const Icon = iconFor(w.icon);
            return (
              <Reveal key={w.title} delay={(i % 2) * 0.06}>
                <div className="flex gap-4 py-5 border-b border-border">
                  <div className="w-11 h-11 rounded-md bg-aqua/10 flex items-center justify-center shrink-0">
                    <Icon className="w-5 h-5 text-aqua" strokeWidth={1.9} />
                  </div>
                  <div>
                    <h3 className="font-bold text-ink">{w.title}</h3>
                    <p className="mt-1 text-sm text-greyish">{w.text}</p>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Partners() {
  const PARTNERS = usePartners();

  return (
    <section className="bg-[#f9fafb] border-y border-border py-16 md:py-20">
      <div className="container-x">
        <Reveal>
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="eyebrow">Our Partners</p>
            </div>
            <Link to="/partners" className="inline-flex items-center gap-1.5 text-sm font-semibold text-ink hover:text-aqua transition-colors">View all partners <ArrowUpRight className="w-4 h-4" /></Link>
          </div>
        </Reveal>
      </div>
      <div className="mt-8">
        {/* Logos, not names. This band rendered the partner names as plain
            text and ignored the `logo` field entirely, so logos uploaded in
            the CMS never appeared here — only on the Our Partners page. A
            partner with no logo yet falls back to its name. */}
        <LogoMarquee items={PARTNERS} />
      </div>
    </section>
  );
}

function ServicesStrip() {
  const SERVICES = useServices();

  return (
    <section className="container-x py-20 md:py-28">
      <Reveal>
        <p className="eyebrow">Our Services</p>
        <h2 className="mt-4 text-3xl md:text-5xl font-bold tracking-tight text-ink max-w-2xl text-balance">Support that goes beyond the sale</h2>
      </Reveal>
      <div className="mt-12 grid md:grid-cols-3 gap-6">
        {SERVICES.map((s, i) => {
          const Icon = iconFor(s.icon);
          return (
            <Reveal key={s.slug} delay={i * 0.08}>
              <Link to={`/services/${s.slug}`} data-testid={`home-service-${s.slug}`} className="group block h-full p-8 rounded-lg border border-border bg-white hover:border-aqua/40 hover:shadow-[0_12px_32px_rgba(0,0,0,0.06)] transition-all">
                <div className="w-12 h-12 rounded-md bg-ink text-white flex items-center justify-center group-hover:bg-aqua transition-colors">
                  <Icon className="w-6 h-6" strokeWidth={1.8} />
                </div>
                <h3 className="mt-6 text-xl font-bold text-ink">{s.name}</h3>
                <p className="mt-2 text-greyish text-sm">{s.short}</p>
                <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-aqua group-hover:gap-3 transition-all">Learn more <ArrowRight className="w-4 h-4" /></span>
              </Link>
            </Reveal>
          );
        })}
      </div>
    </section>
  );
}

export default function Home() {
  const SITE = useSite();
  const IMAGES = useSiteImages();

  return (
    <>
      <Seo
        routeKey="home"
        title="Laboratory Equipment & Scientific Solutions"
        description="Athena Scientific supplies reliable laboratory technologies — water purification systems, glassware washers and balances — with technical expertise and customer-focused support."
        ogImage={OG_IMAGE}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Organization",
          name: SITE.company,
          url: `https://${SITE.website}`,
          email: SITE.email,
          telephone: SITE.mobile,
          address: { "@type": "PostalAddress", streetAddress: SITE.address, addressCountry: "SG" },
        }}
      />
      <Hero />
      <Featured />
      <Partners />
      <Categories />
      <Why />
      <HelpCTA />
      <ServicesStrip />
      <HelpCTA
        variant="dark"
        eyebrow="Ready when you are"
        title="Let's find the right solution for your laboratory."
        body="Tell us about your application, challenge or project. Our team will help you explore the options."
        primaryLabel="Send an Enquiry"
        primaryTo="/enquiry"
        secondaryLabel="Contact Us"
        secondaryTo="/contact"
      />
    </>
  );
}
